import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";

import { useOutletContext } from "react-router-dom";
import useDataSourceManager from "hooks/useDataSourceManager";
import useZustandStore from "zustandStore";

import { cloneDeep, isObject } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { styled, useTheme } from "@mui/material/styles";
import { Grid, Box, Menu, FormGroup, FormControlLabel, Checkbox, Typography } from "@mui/material";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

import { ReactComponent as DisplayClockIcon } from "assets/images/icons/icn_display_clock.svg";
import { ReactComponent as CustomizeIcon } from "assets/images/icons/icn_btn_customize.svg";
import { ReactComponent as CustomizePressedIcon } from "assets/images/icons/icn_btn_customize_pressed.svg";
import { ReactComponent as WidgetIcon } from "assets/images/icons/icn_btn_widget.svg";
import { ReactComponent as SelectedIcon } from "assets/images/icons/icn_selected.svg";
import { ReactComponent as UnselectedIcon } from "assets/images/icons/icn_unselected.svg";

import DrawerField from "components/Drawer";
import GridContainer from "./sections/GridContainer.js";

import { defaultWidgetSet, defaultActionStatus } from "./const";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    marginTop: "12px",
    marginLeft: "2px",
    borderRadius: 12,
    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
    backgroundColor: theme.palette.primary.white,
    maxWidth: 600,
    height: 525,
    border: `1px solid ${theme.palette.divider}`,
    position: "relative",
    overflow: "visible",
  },
  "& .MuiPaper-root::before": {
    content: '""',
    position: "absolute",
    top: "-12px",
    right: "34px",
    width: 0,
    height: 0,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderBottom: `12px solid ${theme.palette.primary.white}`,
    filter:
      "drop-shadow(-2px -1px 2px rgba(0, 0, 0, 0.05)) drop-shadow(2px -1px 2px rgba(0, 0, 0, 0.05))",
    zIndex: 1,
  },
}));

const widgetMenucolumnMap = {
  accessPointsOverview: 1,
  top5SsidByTraffic: 1,
  top5SwitchByTraffic: 1,
  top5ApByTraffic: 1,
  top5PoePower: 1,
  top5Cpu: 1,
  top5Memory: 1,
  wirelessTrafficUsage: 1,
  apChannelUtilization: 1,
  poePower: 1,
  top5WirelessClientsByTraffic: 2,
  wirelessClientsBand: 2,
  top5AccessPointsByWirelessClient: 2,
  wirelessClientsRssi: 2,
  wirelessClientsActivities: 2
};

const WidgetMenu = ({
  anchorEl,
  setAnchorEl,
  timerStart,
  changeActionStatus,
  widgetSet,
  setWidgetSet,
  setTargetWidget,
  selectedBtn,
  setSelectedBtn,
}) => {
  const theme = useTheme();

  const menuList = [
    { name: 'accessPointsOverview', label: "Access Points Overview" },
    { name: 'top5SsidByTraffic', label: "Top 5 SSIDs by Traffic" },
    { name: 'top5SwitchByTraffic', label: "Top 5 Switches by Traffic" },
    { name: 'top5ApByTraffic', label: "Top 5 Access Points by Traffic" },
    { name: 'top5PoePower', label: "Top 5 PoE Power Consumption" },
    { name: 'top5Cpu', label: "Top 5 CPU by Usage" },
    { name: 'top5Memory', label: "Top 5 Memory by Usage" },
    { name: 'wirelessTrafficUsage', label: "Access Point Wireless Traffic Usage" },
    { name: 'apChannelUtilization', label: "Access Points Channel Utilization" },
    { name: 'poePower', label: "PoE Power Consumption" },
    { name: 'top5WirelessClientsByTraffic', label: "Top 5 Wireless Clients by Traffic" },
    { name: 'wirelessClientsBand', label: "Wireless Clients by Band" },
    { name: 'top5AccessPointsByWirelessClient', label: "Top 5 Access Points by Wireless Client" },
    { name: 'wirelessClientsRssi', label: "Wireless Clients by RSSI ≥ 70 dBm" },
    { name: 'wirelessClientsActivities', label: "Wireless Clients Activities" }
  ]

  const handleCloseMenu = () => {
    timerStart()
    setAnchorEl(null);
    setSelectedBtn(false);
    changeActionStatus({
      draggable: false,
      update: { system: true, custom: false },
      reset: { system: false, custom: false },
    });
  };

  const handleWidget = (event) => {
    let targetObj = {}
    targetObj[event.target.name] = {
      id: event.target.id,
      check: event.target.checked
    }
    setTargetWidget({
      id: event.target.id,
      check: event.target.checked
    })
    setWidgetSet({ ...widgetSet, ...targetObj });
  };

  const resetWidget = () => {
    changeActionStatus({
      draggable: false,
      update: { system: false, custom: false },
      reset: { system: true, custom: false },
    });
  };

  const showAllWidget = () => {
    const allSelected = Object.values(widgetSet).every((item) => item.check);
    if (allSelected) return;

    const updatedWidgetSet = {};
    for (const key in widgetSet) {
      updatedWidgetSet[key] = {
        ...widgetSet[key],
        check: true,
      };
    }

    setWidgetSet(updatedWidgetSet);

    changeActionStatus({
      draggable: false,
      update: { system: false, custom: false },
      reset: { system: true, custom: false },
    });
  };

  return (
    <StyledMenu
      open={selectedBtn}
      onClose={handleCloseMenu}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <Box padding="0 20px">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            mb: 1,
          }}
        >
          <Typography
            sx={{
              marginTop: 2,
              fontSize: theme.typography.customFontSize[1],
              fontWeight: theme.typography.customFontWeight[1],
              color: theme.palette.primary.greyPrimary,
            }}
          >
            Network
          </Typography>

          <Typography
            sx={{
              marginTop: 2,
              fontSize: theme.typography.customFontSize[1],
              fontWeight: theme.typography.customFontWeight[1],
              color: theme.palette.primary.greyPrimary,
              textAlign: "left",
            }}
          >
            Client
          </Typography>
        </Box>
        <FormGroup
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gridAutoFlow: "row dense",
            columnGap: 3,
          }}
        >
          {menuList.map((item, index) => (
            <FormControlLabel
              key={`${item.name}-${index}`}
              sx={{
                gridColumn:
                  widgetMenucolumnMap[item.name] === 2 ? "2 / 3" : "1 / 2",
                mt: 0.5,
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: theme.typography.customFontWeight[2],
                  color: "primary.blackPrimary",
                },
              }}
              control={
                <Checkbox
                  icon={<UnselectedIcon />}
                  checkedIcon={<SelectedIcon />}
                  checked={widgetSet[item.name].check}
                  onChange={handleWidget}
                  name={item.name}
                  id={widgetSet[item.name].id}
                />
              }
              label={item.label}
            />
          ))}
        </FormGroup>
      </Box>
      <Box display="flex" justifyContent="center" width="100%" mt={2}>
        <Box sx={{ margin: '10px' }}>
          <OutlineButton width="96px" height="32px" onClick={resetWidget}>
            Reset
          </OutlineButton>
        </Box>
        <Box sx={{ margin: '10px' }}>
          <SolidButton width="96px" height="32px" onClick={showAllWidget}>
            Show All
          </SolidButton>
        </Box>
      </Box>
    </StyledMenu>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const { scopeReady } = useOutletContext();

  const [auth, setAuth] = useState({ realm: null, orgs: null, sites: null });

  const [statsUuid, _setStatsUuid] = useState(uuidv4());
  const { statsData: globalStatsData, setStatsData } = useZustandStore();

  const { dataMap, getData, sendRequest, sendBatch } = useDataSourceManager();

  const [showMenu, setShowMenu] = useState(false);
  const [selectedBtn, setSelectedBtn] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [targetWidget, setTargetWidget] = useState({});
  const [widgetSet, setWidgetSet] = useState(cloneDeep(defaultWidgetSet));
  const [actionStatus, setActionStatus] = useState(cloneDeep(defaultActionStatus));

  const [runTimer, setRunTimer] = useState(true);
  const [fetchDataMode, setFetchDataMode] = useState("");

  const [drawerOpen, setDrawerOpen] = useState({ key: "", open: false, deviceId: "" });

  const [settingsData, setSettingsData] = useState(null);
  const [licenseDataLoaded, setLicenseDataLoaded] = useState(false);
  const settingsUuid = useMemo(() => uuidv4(), []);

  const timerStart = () => {
    setRunTimer(true);
  };

  const timerStop = () => {
    setRunTimer(false);
  };

  const divRef = useRef(null);

  const fetchLicenseData = async () => {
    try {
      const realmId = auth.realm?.id || "";
      const orgId = auth.orgs?.id || "";
      const standalone = !auth.orgs?.id || Object.keys(auth.orgs || {}).length === 0;

      const settingsApiKey = standalone
        ? "/orgs/:orgId/settings/general.read"
        : "/ents/:entId/orgs/:orgId/settings/general.read";

      const restfulParams = standalone
        ? { orgId: realmId }
        : { entId: realmId, orgId };

      const { getSendBatchData } = await sendBatch([
        {
          apiKey: settingsApiKey,
          requestUuid: settingsUuid,
          restfulPlaceholders: restfulParams,
          params: {},
        },
      ]);

      const settingsRes = getSendBatchData({ requestUuid: settingsUuid });

      if (settingsRes?.data) {
        setSettingsData(settingsRes.data);
      }
    } catch (error) {
      console.error("[Dashboard] Error fetching license data via sendBatch:", error);
    } finally {
      setLicenseDataLoaded(true);
    }
  };

  useEffect(() => {
    if (scopeReady) {
      const { realm, orgs, site } = JSON.parse(localStorage.getItem('selectBreadcrumbs'));
      if (isObject(site) && Object.keys(site).length > 0) {
        setAuth((prev) => ({
          ...prev,
          scopeLevel: "site",
          realm,
          orgs,
          sites: site,
          permissions: site.roleName,
        }));
      } else {
        const currentPermissions = realm.roleName || orgs.roleName || "";

        setAuth((prev) => ({
          ...prev,
          scopeLevel: "org",
          realm,
          orgs,
          permissions: currentPermissions,
        }));
      }
      setFetchDataMode('fetchDashboardLayout')
    }
  }, [scopeReady]);

  useEffect(() => {
    if (fetchDataMode === 'fetchDashboardLayout' && (scopeReady || checkMockup === 'true')) {
      if (checkMockup === 'false' && (!auth.realm?.id || !auth.sites?.id)) return;

      const standalone = !auth.orgs?.id || Object.keys(auth.orgs || {}).length === 0;
      const apiStatsUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/stats.read`;

      const restfulParamsConfig = {
        standalone: {
          orgId: auth.realm.id,
          siteId: auth.sites.id,
        },
        nonStandalone: {
          entId: auth.realm.id,
          orgId: auth.orgs.id,
          siteId: auth.sites.id,
        },
      };

      const requestParams = {
        "data": {
          "networkStatus": {},
          "cpuTop5": {},
          "memTop5": {},
          "poeTop5": {},
          "pocConsumption": {},
          "top5TrafficApSsid": {},
          "top5TrafficSwitch": {},
          "top5TrafficAp": {},
          "apChannelUtilization": {},
          "wirelessTrafficUsage": {},
          "accessPointsOverview": {},
          "wirelessClientsRssi": {},
          "wirelessClientsBand": {},
          "top5TrafficWirelessClients": {},
          "top5WirelessClientsAp": {},
          "wirelessClientsActivities": {}
        }
      };

      const mode = standalone ? "standalone" : "nonStandalone";
      const restfulParams = restfulParamsConfig[mode];

      let timer;
      if (runTimer) {
        timer = setInterval(() => {
          sendRequest(apiStatsUrl, statsUuid, restfulParams, requestParams);
        }, 30000);
      }

      return () => {
        if (timer) clearInterval(timer);
      };
    }
  }, [scopeReady, fetchDataMode, runTimer])

  useEffect(() => {
    if (showMenu && checkMockup === "false" && !licenseDataLoaded && auth.realm?.id) {
      fetchLicenseData();
    }
  }, [showMenu, licenseDataLoaded, auth]);

  useEffect(() => {
    if (!runTimer && fetchDataMode === 'intervalFetch') {
      setFetchDataMode('');
    }
  }, [runTimer]);

  useEffect(() => {
    const response = getData(statsUuid);
    if (response?.data) {
      setStatsData(response.data);
    }
  }, [dataMap, statsUuid, setStatsData, getData]);

  const handleClickMenu = (event) => {
    timerStop()
    setAnchorEl(event.currentTarget);
    setSelectedBtn(true);
    changeActionStatus({
      draggable: false,
      update: { system: false, custom: false },
      reset: { system: false, custom: false },
    });
  };

  const handleCustom = () => {
    timerStop()
    changeActionStatus({
      draggable: true,
      update: { system: false, custom: false },
      reset: { system: false, custom: false },
    });
  };

  const saveSetting = () => {
    timerStart()
    changeActionStatus({
      draggable: false,
      update: { system: false, custom: true },
      reset: { system: false, custom: false },
    });
  };

  const closeSetting = () => {
    timerStart()
    changeActionStatus({
      draggable: false,
      update: { system: false, custom: false },
      reset: { system: false, custom: true },
    });
  };

  const changeActionStatus = useCallback((updatedStatus) => {
    setActionStatus((prevStatus) => ({ ...prevStatus, ...updatedStatus }));
  }, []);

  function openDeviceDrawer(key, open, deviceId) {
    setDrawerOpen({ key, open, deviceId })
  }

  return (
    <><div ref={divRef}>
      <div className="menuComponent"
        style={{ position: "absolute", top: "-5px", right: "15px", zIndex: "999" }}
      >
        {showMenu && (
          <Grid container>
            {!actionStatus.draggable && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DisplayClockIcon />
                <Typography sx={{ marginLeft: "5px" }}>Last 24 Hours</Typography>
              </Box>
            )}
            {actionStatus.draggable && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CustomizePressedIcon />
                <Typography sx={{ marginLeft: "5px", fontWeight: theme.typography.customFontWeight[1] }}>
                  Customize Widgets:
                </Typography>
              </Box>
            )}
            {!actionStatus.draggable && (
              <Box sx={{ marginLeft: "10px" }}>
                <SolidButton
                  style={{ fontWeight: theme.typography.customFontWeight[2] }}
                  startIcon={<CustomizeIcon />}
                  width="100%"
                  onClick={() => handleCustom()}
                >
                  Customize
                </SolidButton>
              </Box>
            )}
            {actionStatus.draggable && (
              <>
                <Box sx={{ margin: "0 10px" }}>
                  <OutlineButton width="100px" onClick={() => closeSetting()}>
                    Cancel
                  </OutlineButton>
                </Box>
                <Box>
                  <SolidButton width="100px" onClick={() => saveSetting()}>
                    Save
                  </SolidButton>
                </Box>
              </>
            )}
            {!actionStatus.draggable && (
              <Box style={{ marginLeft: "10px" }}>
                <SolidButton
                  style={{
                    fontWeight: theme.typography.customFontWeight[2],
                    ...(selectedBtn && {
                      backgroundColor: theme.palette.primary.mainHover,
                      border: `1px solid ${theme.palette.primary.main}`
                    }),
                  }}
                  startIcon={<WidgetIcon />}
                  width="100%"
                  onClick={handleClickMenu}
                >
                  Widget
                </SolidButton>
              </Box>
            )}
            <WidgetMenu
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              selectedBtn={selectedBtn}
              setSelectedBtn={setSelectedBtn}
              timerStart={timerStart}
              changeActionStatus={changeActionStatus}
              widgetSet={widgetSet}
              setWidgetSet={setWidgetSet}
              setTargetWidget={setTargetWidget}
            />
          </Grid>)}
      </div>
      <div className="dndComponent" style={{ position: "relative", width: "calc(100% + 40px)", left: "-20px", }} >
        <GridContainer
          statsData={globalStatsData}
          fetchDataMode={fetchDataMode}
          setFetchDataMode={setFetchDataMode}
          targetWidget={targetWidget}
          widgetSet={widgetSet}
          setWidgetSet={setWidgetSet}
          setShowMenu={setShowMenu}
          actionStatus={actionStatus}
          openDeviceDrawer={openDeviceDrawer}
        />
      </div>
      {drawerOpen.open && drawerOpen.key === "devices" &&
        <DrawerField
          contentKey={drawerOpen.deviceId}
          open={drawerOpen.open}
          width="auto"
          handleClose={() => setDrawerOpen("", false, "")}
          commonContent="devices"
          contentProps={{
            fetchFlag: true,
            auth: auth,
            deviceId: drawerOpen.deviceId,
            settingsData,
          }}
        />}
      {drawerOpen.open && drawerOpen.key === "clients" &&
        <DrawerField
          contentKey={`clients_${uuidv4()}`}
          open={drawerOpen.open}
          width="550px"
          handleClose={() => setDrawerOpen("", false, "")}
          commonContent="clients"
          contentProps={{
            auth: auth,
            selectedClientId: drawerOpen.deviceId,
          }}
        />}
    </div></>
  );
};

export default Dashboard;
